/**
 * 认证接口封装（登录 / 初始化管理员 / 会话 / 改密码）。
 *
 * 约定（与服务端契约一致）：
 *   · 会话走 HttpOnly Cookie，前端既读不到也不需要读；
 *   · CSRF token 只保存在**内存**里（模块级变量），严禁写入 localStorage / sessionStorage —— 项目硬规则；
 *   · 所有请求都带 `credentials: 'same-origin'`，否则 Cookie 不会随请求发出。
 *
 * 与 client.ts 的关系：client.ts 从这里取 CSRF token、并在收到 401 时通知本模块，
 * 因此 auth.ts **不能**反过来 import client.ts（否则循环依赖）。
 */

export interface AuthUser {
  id: number
  username: string
  displayName: string
  isAdmin: boolean
}

/** GET /api/auth/state */
export interface AuthStateInfo {
  initialized: boolean
  authenticated: boolean
  user: AuthUser | null
}

/** 登录 / 初始化 / 取当前用户 的成功返回 */
export interface AuthSession {
  user: AuthUser
  csrf: string
}

/** 认证相关错误：带 HTTP 状态与服务端错误码（如 invalid_credentials / too_many_attempts） */
export class AuthError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message?: string,
  ) {
    super(message ?? code)
    this.name = 'AuthError'
  }
}

/* ---------------------------------------------------------------------------
 * CSRF token：只放内存
 * ------------------------------------------------------------------------- */

let csrfToken = ''

/** 取当前 CSRF token（未登录时为空串） */
export function getCsrfToken(): string {
  return csrfToken
}

/** 由登录 / 初始化 / 取当前用户的返回设置 CSRF token */
export function setCsrfToken(token: string): void {
  csrfToken = token
}

export function clearCsrfToken(): void {
  csrfToken = ''
}

/* ---------------------------------------------------------------------------
 * 401 通知：client.ts 收到 401 时回调，由 AuthContext 决定怎么处理（通常是清空用户态）
 * ------------------------------------------------------------------------- */

let unauthorizedHandler: (() => void) | null = null

export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn
}

/** 供 client.ts 调用：通知"会话已失效" */
export function notifyUnauthorized(): void {
  unauthorizedHandler?.()
}

/* ---------------------------------------------------------------------------
 * 请求helper
 * ------------------------------------------------------------------------- */

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH'
  body?: unknown
  /** 为 true 时带 X-CSRF-Token（写操作） */
  withCsrf?: boolean
}

async function authRequest<T>(url: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (options.withCsrf === true && csrfToken !== '') headers['X-CSRF-Token'] = csrfToken

  let res: Response
  try {
    res = await fetch(url, {
      method: options.method ?? 'GET',
      headers,
      credentials: 'same-origin',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    })
  } catch (e) {
    throw new AuthError(0, 'network_error', e instanceof Error ? e.message : String(e))
  }

  if (!res.ok) {
    let code = ''
    try {
      const payload = (await res.json()) as { error?: string; message?: string }
      code = payload.error ?? payload.message ?? ''
    } catch {
      // 非 JSON 响应（例如 SPA 回退的 HTML）忽略
    }
    throw new AuthError(res.status, code || `http_${res.status}`)
  }

  // 部分接口成功时也可能没有 body（例如 logout 返回 { ok: true }，这里统一按 JSON 解析）
  try {
    return (await res.json()) as T
  } catch {
    return {} as T
  }
}

/* ---------------------------------------------------------------------------
 * 对外接口
 * ------------------------------------------------------------------------- */

/** 查询系统初始化状态与当前登录状态（不需要登录） */
export function getAuthState(): Promise<AuthStateInfo> {
  return authRequest<AuthStateInfo>('/api/auth/state')
}

/** 初始化管理员（仅限本机、且系统尚无任何用户时可用） */
export async function initAdmin(input: {
  username: string
  password: string
  displayName?: string
}): Promise<AuthSession> {
  const res = await authRequest<AuthSession>('/api/auth/init', { method: 'POST', body: input })
  setCsrfToken(res.csrf)
  return res
}

/** 用户名密码登录 */
export async function login(username: string, password: string): Promise<AuthSession> {
  const res = await authRequest<AuthSession>('/api/auth/login', {
    method: 'POST',
    body: { username, password },
  })
  setCsrfToken(res.csrf)
  return res
}

/** 退出登录（同时丢弃内存里的 CSRF token） */
export async function logout(): Promise<void> {
  try {
    await authRequest<{ ok: boolean }>('/api/auth/logout', { method: 'POST', withCsrf: true })
  } finally {
    clearCsrfToken()
  }
}

/** 取当前登录用户；未登录时抛 AuthError(status=401) */
export async function fetchMe(): Promise<AuthSession> {
  const res = await authRequest<AuthSession>('/api/auth/me')
  setCsrfToken(res.csrf)
  return res
}

/** 修改密码（需已登录 + CSRF） */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await authRequest<{ ok?: boolean }>('/api/auth/password', {
    method: 'POST',
    body: { currentPassword, newPassword },
    withCsrf: true,
  })
}

/**
 * 修改自己的显示名（需已登录 + CSRF）。
 *
 * 返回更新后的用户对象；调用方拿到后应刷新 AuthContext，让顶栏显示名同步。
 */
export async function updateProfile(displayName: string): Promise<AuthUser> {
  const res = await authRequest<{ user: AuthUser }>('/api/auth/profile', {
    method: 'PATCH',
    body: { displayName },
    withCsrf: true,
  })
  return res.user
}
